import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, Loader2 } from "lucide-react";
import PersonModal from "@/components/PersonModal";
import { Person, PersonCategory } from "@/types";
import { peopleAPI } from "@/api/people";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { getCurrencySymbol } from "@/utils/currencyUtils";
import { currencyAPI } from "@/api/currency";

interface PeopleTabProps {
  projectId: string;
  projectStatus?: "active" | "on-track" | "delayed" | "completed";
}

const PeopleTab: React.FC<PeopleTabProps> = ({ projectId, projectStatus = "active" }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  
  const isProjectCompleted = projectStatus === "completed";
  
  // Get the preferred currency and its symbol
  const preferredCurrency = preferences?.base_currency || "TRY";
  const currencySymbol = getCurrencySymbol(preferredCurrency);

  // Fetch people data when component mounts or projectId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        

        // Create a retry function to handle potential API failures
        const fetchWithRetry = async (fetchFn, errorMessage, retryCount = 2) => {
          let attempts = 0;
          let lastError = null;
          
          while (attempts < retryCount) {
            try {
              return await fetchFn();
            } catch (err) {
              
              lastError = err;
              attempts++;
              // Wait a moment before retrying
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
          
          
          return []; // Return empty array on failure after retries
        };
        
        // Call the API to get people for this project with retry
        const assignedPeople = await fetchWithRetry(
          () => peopleAPI.getPeopleByProject(projectId),
          "All attempts to fetch assigned people failed:"
        );
        
        
        
        // Also get people who have received payments from this project with retry
        const peopleWithPayments = await fetchWithRetry(
          () => peopleAPI.getPeopleWithPaymentsByProject(projectId),
          "All attempts to fetch people with payments failed:"
        );
        
        
        
        // Merge both lists without duplicates
        const uniquePeopleMap = new Map();
        
        try {
          // First add all assigned people
          if (Array.isArray(assignedPeople)) {
            assignedPeople.forEach(person => {
              if (person && person.id) {
                uniquePeopleMap.set(person.id, person);
              }
            });
          }
          
          // Then add or update people with payments
          if (Array.isArray(peopleWithPayments)) {
            peopleWithPayments.forEach(person => {
              if (!person || !person.id) return;
              
              if (uniquePeopleMap.has(person.id)) {
                // Person already exists, update payment information
                const existingPerson = uniquePeopleMap.get(person.id);
                uniquePeopleMap.set(person.id, {
                  ...existingPerson,
                  totalPaid: Math.max(existingPerson?.totalPaid || 0, person.totalPaid || 0)
                });
              } else {
                // Add new person
                uniquePeopleMap.set(person.id, person);
              }
            });
          }
          
          // Convert map to array
          const mergedPeople = Array.from(uniquePeopleMap.values());
          
          
          // Sort by name
          mergedPeople.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          
          // Add conversion to preferred currency if from the base currency
          if (mergedPeople.length > 0) {
            
            
            // Get exchange rates
            const exchangeRates = await currencyAPI.getExchangeRates();
            
            
            // Process each person
            const convertedPeople = await Promise.all(mergedPeople.map(async person => {
              // Only process if person has totalAgreed or totalPaid
              if (!person.totalAgreed && !person.totalPaid) return person;
              
              // Get original currency
              const fromCurrency = person.original_currency || "TRY";
              const toCurrency = preferredCurrency;
              
              // If currencies are the same, no conversion needed
              if (fromCurrency === toCurrency) return person;
              
              try {
                // Only use convertCurrency method as it's available in the API
                
                // Convert totalAgreed
                let convertedTotalAgreed = person.totalAgreed || 0;
                if (person.totalAgreed && fromCurrency !== toCurrency) {
                  const { data } = await currencyAPI.convertCurrency(
                    person.totalAgreed,
                    fromCurrency,
                    toCurrency
                  );
                  convertedTotalAgreed = data || person.totalAgreed;
                }
                
                // Convert totalPaid
                let convertedTotalPaid = person.totalPaid || 0;
                if (person.totalPaid && fromCurrency !== toCurrency) {
                  const { data } = await currencyAPI.convertCurrency(
                    person.totalPaid,
                    fromCurrency,
                    toCurrency
                  );
                  convertedTotalPaid = data || person.totalPaid;
                }
                
                // Return person with converted values
                return {
                  ...person,
                  totalAgreed: convertedTotalAgreed,
                  totalPaid: convertedTotalPaid,
                  original_currency: fromCurrency,
                  currency: toCurrency
                };
              } catch (error) {
                
                // Return original person on conversion error
                return person;
              }
            }));
            
            setPeople(convertedPeople);
            
          } else {
            setPeople(mergedPeople);
            
          }
        } catch (error) {
          
          setPeople([]);
        }
      } catch (error) {
        
        setError(t("people.people_loading_error"));
        setPeople([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [projectId, preferredCurrency, preferences]);

  const filteredPeople = people.filter(
    (person) =>
      person.name.toLowerCase().includes(filter.toLowerCase()) ||
      person.category.toLowerCase().includes(filter.toLowerCase()) ||
      (person.specialty && person.specialty.toLowerCase().includes(filter.toLowerCase()))
  );

  const handleAddPerson = async (data: any) => {
    try {
      // Create the person in the database
      
      const newPerson = await peopleAPI.createPerson({
        name: data.name,
        category: data.category,
        specialty: data.specialty,
        phone: data.phone,
        email: data.email,
        created_by: user.id
      });
      
      
      
      // Add the person to the project with agreed amount
      
      await peopleAPI.addPersonToProject(
        projectId,
        newPerson.id,
        data.totalAgreed,
        data.totalPaid
      );
      
      // Add the new person to the state
      setPeople(prevPeople => [...prevPeople, {
        id: newPerson.id,
        name: newPerson.name,
        category: newPerson.category as PersonCategory,
        specialty: newPerson.specialty,
        totalAgreed: data.totalAgreed || 0,
        totalPaid: data.totalPaid || 0
      }]);
      
      toast.success(t("people.person_added_successfully", { name: data.name }));
      setIsModalOpen(false);
    } catch (error) {
      
      
      // Try to provide a more informative error message
      let errorMessage = t("people.person_add_error");
      
      if (error.message) {
        if (error.message.includes("duplicate")) {
          errorMessage = t("people.person_duplicate_error");
        } else if (error.message.includes("already exists")) {
          errorMessage = t("people.person_already_exists");
        } else if (error.message.includes("violates foreign key constraint")) {
          errorMessage = t("people.person_database_error");
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handlePersonClick = (personId: string) => {
    navigate(`/project/${projectId}/person/${personId}`);
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'laborer':
        return t('people.category_labels.laborer');
      case 'supplier':
        return t('people.category_labels.supplier');
      case 'subcontractor':
        return t('people.category_labels.subcontractor');
      default:
        return category;
    }
  };

  const formatCurrencyValue = (value: number): string => {
    if (!value) return "0";
    
    // Set decimal places based on currency
    let decimalPlaces = 2; // Default for most currencies
    if (preferredCurrency === 'XAU') decimalPlaces = 4;
    if (preferredCurrency === 'BTC') decimalPlaces = 6;
    
    return value.toLocaleString('tr-TR', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    });
  };

  return (
    <div className="pt-2">
      <div className="flex space-x-2 mb-4">
        <div className="flex-1">
          <Input
            placeholder={t("people.search_people")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full border-gray-200 rounded-lg bg-white"
          />
        </div>
        <Button
          className="bg-brand-600 hover:bg-brand-700 px-4"
          onClick={() => setIsModalOpen(true)}
          disabled={isProjectCompleted || isLoading}
          title={isProjectCompleted ? t("people.completed_project_warning") : ""}
        >
          <UserPlus className="w-4 h-4 mr-2" /> {t("common.add")}
        </Button>
      </div>

      {isProjectCompleted && (
        <div className="mb-4 bg-warning-50 border border-warning-300 text-warning-600 rounded-xl px-4 py-2 text-sm">
          {t("people.completed_project_warning")}
        </div>
      )}

      <div className="mb-4 bg-brand-50 border border-brand-100 text-brand-600 rounded-xl px-4 py-2 text-[13px]">
        {t("people.people_tab_description")}
      </div>

      {isLoading ? (
        <div className="flex flex-col justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600 mb-4" />
          <p className="text-gray-500">{t("people.loading_people")}</p>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <div className="text-error-600 mb-4">{error}</div>
          <Button
            variant="outline"
            onClick={() => {
              setIsLoading(true);
              setError(null);
              setTimeout(() => {
                window.location.reload();
              }, 500);
            }}
          >
            <Loader2 className="h-4 w-4 mr-2" />
            {t("people.reload_people")}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPeople.length > 0 ? (
            filteredPeople.map((person) => (
              <div
                key={person.id}
                className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handlePersonClick(person.id)}
              >
                <div className="flex justify-between items-center gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-[15px]">{person.name}</h3>
                    <p className="text-[13px] text-gray-500 mt-0.5">
                      {getCategoryLabel(person.category)}
                      {person.specialty && ` · ${person.specialty}`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    {person.totalAgreed > 0 && (
                      <>
                        <p className="text-[11px] text-gray-500">
                          {t("people.amount_fields.agreed")}: <span className="font-mono font-medium text-gray-700">{formatCurrencyValue(person.totalAgreed)} {currencySymbol}</span>
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {t("people.amount_fields.paid")}: <span className="font-mono font-medium text-success-600">{formatCurrencyValue(person.totalPaid)} {currencySymbol}</span>
                        </p>
                        <p className="text-[12px] font-semibold text-error-600">
                          {t("people.amount_fields.remaining")}: <span className="font-mono">{formatCurrencyValue(person.totalAgreed - person.totalPaid)} {currencySymbol}</span>
                        </p>
                      </>
                    )}
                    {person.totalAgreed === 0 && person.totalPaid > 0 && (
                      <p className="text-[12px] font-semibold text-success-600">
                        {t("people.amount_fields.paid")}: <span className="font-mono">{formatCurrencyValue(person.totalPaid)} {currencySymbol}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">{t("people.no_people_found")}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsLoading(true);
                  setError(null);
                  setTimeout(() => {
                    window.location.reload();
                  }, 500);
                }}
              >
                <Loader2 className="h-4 w-4 mr-2" />
                {t("people.reload_people")}
              </Button>
            </div>
          )}
        </div>
      )}

      <PersonModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddPerson}
        isEditing={false}
        projectId={projectId}
      />
    </div>
  );
};

export default PeopleTab;
